/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import { GameState, ItemReference } from "@shared/types";
import styles from "../../styles";
import { GameRouteService } from "../../services/GameRouteService";
import { GameStateService } from "../../services/GameStateService";

interface PopupElement extends HTMLElement {
    content: string;
}

/**
 * Component for rendering the player's inventory
 */
export class InventoryComponent extends HTMLElement {
    private _items: ItemReference[] = [];
    private _selectedItems: Set<ItemReference> = new Set<ItemReference>();
    private _gameRouteService: GameRouteService = new GameRouteService();
    private _gameStateService: GameStateService = GameStateService.getInstance();
    private _unsubscribe: (() => void) | null = null;

    /**
     * Set the inventory items
     */
    public set items(items: ItemReference[]) {
        this._items = items;
        this.render();
    }

    /**
     * Set the selected items
     */
    public set selectedItems(items: Set<ItemReference>) {
        this._selectedItems = items;
        this.render();
    }

    /**
     * Called when the element is connected to the DOM
     */
    public connectedCallback(): void {
        this.render();
        this.setupEventListeners();
        this._unsubscribe = this._gameStateService.subscribe(this.handleGameStateChange.bind(this));
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    public disconnectedCallback(): void {
        this.removeEventListeners();
        if (this._unsubscribe) {
            this._unsubscribe();
            this._unsubscribe = null;
        }
    }

    /**
     * Handle game state changes
     */
    private handleGameStateChange(started: boolean): void {
        this.setAttribute("data-started", started.toString());
    }

    /**
     * Render the component
     */
    private render(): void {
        const riskAnimation: string = this.calculateTotalRisk() > 10 ? "risk-flashing" : "";

        const content: string = `
            <style>${styles.inventory}</style>
            <div class="inventory">
                <div class="inventory-header">
                    <div class="inventory-title">
                        Inventory
                        <div class="inventory-stats">
                            <span class="stats-count">${this._items.length} item${this._items.length === 1 ? "" : "s"}</span>
                            <div class="risk-summary">
                                <span class="risk-summary-label">Total Risk:</span>
                                <span class="risk-summary-value ${this.getTotalRiskClass()} ${riskAnimation}" 
                                      style="${this.getRiskFactorStyle(this.calculateTotalRisk())}">
                                    ${this.calculateTotalRisk().toFixed(1)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                ${this.renderInventoryItems()}
            </div>
        `;
        this.innerHTML = content;
    }

    /**
     * Calculate the total risk of all inventory items
     */
    private calculateTotalRisk(): number {
        return this._items.reduce((sum, item) => sum + (item.riskFactor || 0), 0);
    }

    /**
     * Get the CSS class for the total risk
     */
    private getTotalRiskClass(): string {
        return this.getRiskFactorClass(this.calculateTotalRisk());
    }

    /**
     * Render the inventory items
     */
    private renderInventoryItems(): string {
        if (this._items.length === 0) {
            return "<div class=\"inventory-empty\">Your inventory is empty.</div>";
        }

        let itemsHtml: string = "<div class=\"inventory-items\">";

        for (const item of this._items) {
            const isSelected: boolean = this._selectedItems.has(item);
            const selectedClass: string = isSelected ? "selected" : "";

            // Calculate risk factor color and animation
            const riskFactor: number = item.riskFactor || 0;
            const riskClass: string = this.getRiskFactorClass(riskFactor);
            const riskStyle: string = this.getRiskFactorStyle(riskFactor);
            const riskAnimation: string = riskFactor > 10 ? "risk-flashing" : "";

            itemsHtml += `
                <div class="inventory-item ${selectedClass}" data-alias="${item.alias}">
                    <img src="${item.image}" alt="${item.name}" class="inventory-item-image">
                    <div class="inventory-item-name">${item.name}</div>
                    <div class="risk-factor-container">
                        <div class="risk-factor-label">Risk:</div>
                        <div class="risk-factor-value ${riskClass} ${riskAnimation}" style="${riskStyle}">${riskFactor.toFixed(1)}</div>
                    </div>
                </div>
            `;
        }

        itemsHtml += "</div>";
        return itemsHtml;
    }

    /**
     * Get the CSS class for a risk factor value
     */
    private getRiskFactorClass(riskFactor: number): string {
        if (riskFactor > 10) return "risk-extreme";
        if (riskFactor >= 8) return "risk-high";
        if (riskFactor >= 5) return "risk-medium";
        if (riskFactor >= 2) return "risk-low";
        return "risk-minimal";
    }

    /**
     * Get the inline style for a risk factor value
     */
    private getRiskFactorStyle(riskFactor: number): string {
        // Calculate a color gradient from green (0) to red (10+)
        let red: number = 0;
        let green: number = 0;

        if (riskFactor <= 5) {
            // 0-5: Green to Yellow (increase red)
            green = 255;
            red = Math.round((riskFactor / 5) * 255);
        }
        else if (riskFactor <= 10) {
            // 5-10: Yellow to Red (decrease green)
            red = 255;
            green = Math.round(255 * (1 - ((riskFactor - 5) / 5)));
        }
        else {
            // 10+: Full red
            red = 255;
            green = 0;
        }

        return `background-color: rgb(${red}, ${green}, 0);`;
    }

    /**
     * Setup event listeners for inventory items
     */
    private setupEventListeners(): void {
        this.addEventListener("click", this.handleItemClick.bind(this));
    }

    /**
     * Remove event listeners
     */
    private removeEventListeners(): void {
        this.removeEventListener("click", this.handleItemClick.bind(this));
    }

    /**
     * Handle inventory item click
     */
    private handleItemClick(event: MouseEvent): void {
        const target: HTMLElement = event.target as HTMLElement;
        const itemElement: HTMLElement | null = target.closest(".inventory-item") as HTMLElement;

        if (!itemElement) {
            return;
        }

        const itemAlias: string | undefined = itemElement.dataset.alias;
        if (!itemAlias) {
            return;
        }

        const item: ItemReference | undefined = this._items.find(i => i.alias === itemAlias);
        if (!item) {
            return;
        }

        // Show popup with item details
        void this.showItemPopup(item);
    }

    /**
     * Show a popup with item details
     */
    private async showItemPopup(item: ItemReference): Promise<void> {
        // Create popup element
        const popupElement: PopupElement = document.createElement("game-popup") as PopupElement;

        // Find content of item
        let content: string = "";
        try {
            const state: GameState | undefined = await this._gameRouteService.executeAction("examine", [item.alias]);
            if (state?.type === "default") {
                content = state.text.join("<br>");
            }
        }
        catch (error) {
            console.error("[error][InventoryComponent::showItemPopup]", error);
            content = "Failed to load item details.";
        }

        // Generate risk factor display with color
        const riskClass: string = this.getRiskFactorClass(item.riskFactor);
        const riskStyle: string = this.getRiskFactorStyle(item.riskFactor);
        const riskAnimation: string = item.riskFactor > 10 ? "risk-flashing" : "";

        // Create popup content with item details
        const popupContent: string = `
            <div class="item-popup-container">
                <h2 class="item-popup-title">${item.name}</h2>
                
                ${item.image
? `
                    <div class="item-popup-image-container">
                        <img src="${item.image}" alt="${item.name}" class="item-popup-image">
                    </div>
                `
: ""}
                
                <div class="item-popup-details">
                    <div class="item-popup-risk">
                        <span class="item-popup-risk-label">Risk Factor:</span>
                        <span class="item-popup-risk-value ${riskClass} ${riskAnimation}" 
                              style="${riskStyle}">${item.riskFactor.toFixed(1)}</span>
                    </div>
                    
                    <div class="item-popup-description">
                        ${content || "No description available."}
                    </div>
                </div>
            </div>
        `;

        // Set popup content
        popupElement.content = popupContent;

        // Add popup to DOM
        document.body.appendChild(popupElement);
    }
}
